// ─── SUI Transaction Builders ────────────────────────────────────────────
// Build TransactionBlocks for AGU staking, governance, treasury
import { Transaction } from '@mysten/sui/transactions';
import { PACKAGE_ID, OBJECTS, AGU_TOKEN_TYPE, parseAGU } from './constants';

// ═══════════════════════════════════════════════════════════════════════════
// STAKING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a stake AGU transaction
 * Calls: staking::stake(pool, coin, clock, ctx)
 */
export function buildStakeTx(coinObjectId: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::staking::stake`,
    arguments: [
      tx.object(OBJECTS.stakingPool),
      tx.object(coinObjectId),
      tx.object('0x6'), // SUI Clock object
    ],
  });
  return tx;
}

/**
 * Build a stake AGU transaction with amount (splits coin first)
 */
export function buildStakeAmountTx(amount: number, coinObjectId: string): Transaction {
  const tx = new Transaction();
  const aguAmount = parseAGU(amount);

  // Split the required amount from the coin
  const [stakeCoin] = tx.splitCoins(tx.object(coinObjectId), [tx.pure.u64(aguAmount)]);

  tx.moveCall({
    target: `${PACKAGE_ID}::staking::stake`,
    arguments: [
      tx.object(OBJECTS.stakingPool),
      stakeCoin,
      tx.object('0x6'),
    ],
  });
  return tx;
}

/**
 * Build an unstake transaction
 * Calls: staking::unstake(pool, stake_receipt, clock, ctx)
 */
export function buildUnstakeTx(stakeReceiptId: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::staking::unstake`,
    arguments: [
      tx.object(OBJECTS.stakingPool),
      tx.object(stakeReceiptId),
      tx.object('0x6'),
    ],
  });
  return tx;
}

/**
 * Build a claim reward transaction
 * Calls: staking::claim_reward(pool, stake_receipt, clock, ctx)
 */
export function buildClaimRewardTx(stakeReceiptId: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::staking::claim_reward`,
    arguments: [
      tx.object(OBJECTS.stakingPool),
      tx.object(stakeReceiptId),
      tx.object('0x6'),
    ],
  });
  return tx;
}

// ═══════════════════════════════════════════════════════════════════════════
// GOVERNANCE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a create proposal transaction
 * Calls: governance::create_proposal(config, title, description, ctx)
 */
export function buildCreateProposalTx(title: string, description: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::governance::create_proposal`,
    arguments: [
      tx.object(OBJECTS.governanceConfig),
      tx.pure.string(title),
      tx.pure.string(description),
    ],
  });
  return tx;
}

/**
 * Build a vote transaction
 * Calls: governance::vote(config, proposal_id, vote_for, ctx)
 */
export function buildVoteTx(proposalId: string, voteFor: boolean): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::governance::vote`,
    arguments: [
      tx.object(OBJECTS.governanceConfig),
      tx.pure.u64(BigInt(proposalId)),
      tx.pure.bool(voteFor),
    ],
  });
  return tx;
}

/**
 * Build a finalize proposal transaction
 * Calls: governance::finalize_proposal(config, proposal_id, clock, ctx)
 */
export function buildFinalizeProposalTx(proposalId: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::governance::finalize_proposal`,
    arguments: [
      tx.object(OBJECTS.governanceConfig),
      tx.pure.u64(BigInt(proposalId)),
      tx.object('0x6'),
    ],
  });
  return tx;
}

// ═══════════════════════════════════════════════════════════════════════════
// TREASURY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a treasury deposit transaction
 * Calls: agu_treasury::deposit(treasury, coin, ctx)
 */
export function buildTreasuryDepositTx(coinObjectId: string): Transaction {
  const tx = new Transaction();
  tx.moveCall({
    target: `${PACKAGE_ID}::agu_treasury::deposit`,
    typeArguments: [AGU_TOKEN_TYPE],
    arguments: [
      tx.object(OBJECTS.aguTreasury),
      tx.object(coinObjectId),
    ],
  });
  return tx;
}

// ═══════════════════════════════════════════════════════════════════════════
// MARKET / SETTLEMENT (for frontend-market integration)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a task creation with AGU payment
 */
export function buildCreateTaskTx(
  title: string,
  description: string,
  reward: number,
  coinObjectId: string,
): Transaction {
  const tx = new Transaction();
  const aguReward = parseAGU(reward);

  const [paymentCoin] = tx.splitCoins(tx.object(coinObjectId), [tx.pure.u64(aguReward)]);

  tx.moveCall({
    target: `${PACKAGE_ID}::task::create_task`,
    arguments: [
      tx.object(OBJECTS.marketConfig),
      tx.pure.string(title),
      tx.pure.string(description),
      paymentCoin,
    ],
  });
  return tx;
}

/**
 * Build an AGU transfer transaction
 */
export function buildTransferAGUTx(
  amount: number,
  recipient: string,
  coinObjectId: string,
): Transaction {
  const tx = new Transaction();
  const aguAmount = parseAGU(amount);

  const [sendCoin] = tx.splitCoins(tx.object(coinObjectId), [tx.pure.u64(aguAmount)]);
  tx.transferObjects([sendCoin], tx.pure.address(recipient));

  return tx;
}
